import { inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/operators';
import { Draft, produce } from 'immer';
import {
  catchError,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';

import {
  ApiAuthorizationType,
  ColumnInfo,
  OAuth2Authorization,
} from '@blaze-shared/api-contracts-domain';
import { OAuthService } from '@blaze-shared/auth-data-access';
import { ApiBuilderApiService } from '@blaze-shared/connectors-data-access';
import {
  ConnectorConfiguration,
  CreateRestApiConnectorRequest,
  OAuthInfo,
  TestRestApiRequest,
} from '@blaze-shared/connectors-domain';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { DialogService } from '@blaze-shared/shared-feature';

import {
  ApiActionStatus,
  ApiBuilderAction,
  ApiBuilderCommonConfig,
  ApiBuilderDialogPage,
  ApiBuilderTestParameterDefinition,
  DEFAULT_API_BUILDER_ACTION,
} from '../api-builder.model';
import { ApiBuilderService } from '../services/api-builder.service';

export interface ApiBuilderState {
  connectorId?: string;
  connectorName: string;
  page: ApiBuilderDialogPage;
  common: ApiBuilderCommonConfig;
  actions: ApiBuilderAction[];
  editingActionId?: ApiBuilderAction['id'];
  originalConnector: ConnectorConfiguration | null;
  coupledConnector: ConnectorConfiguration | null;
  savingConnector: boolean;
}

export const initialState: ApiBuilderState = {
  connectorId: undefined,
  connectorName: '',
  page: ApiBuilderDialogPage.Builder,
  common: {
    baseUrl: '',
    headers: [],
    apiAuthorizationType: ApiAuthorizationType.NoAuth,
  },
  actions: [{ ...DEFAULT_API_BUILDER_ACTION }],
  editingActionId: undefined,
  originalConnector: null,
  coupledConnector: null,
  savingConnector: false,
};

const getActionIndex = (draft: Draft<ApiBuilderState>, payloadAction: ApiBuilderAction): number => {
  return draft.actions.findIndex((action) => action.id === payloadAction.id);
};

@Injectable()
@UntilDestroy()
export class ApiBuilderStore extends ComponentStore<ApiBuilderState> {
  private readonly apiBuilderApiService = inject(ApiBuilderApiService);
  private readonly apiBuilderService = inject(ApiBuilderService);
  private readonly dialogService = inject(DialogService);
  private readonly notificationService = inject(NotificationService);
  private readonly oAuthService = inject(OAuthService);

  // SELECTORS
  readonly connectorId$: Observable<string | undefined> = this.select((state) => state.connectorId);
  readonly common$: Observable<ApiBuilderCommonConfig> = this.select((state) => state.common);
  readonly actions$: Observable<ApiBuilderAction[]> = this.select((state) => state.actions);
  readonly getActionById$ = (actionId: number): Observable<ApiBuilderAction | undefined> =>
    this.actions$.pipe(map((actions) => actions.find((a) => a.id === actionId)));
  readonly currentPage$: Observable<ApiBuilderDialogPage> = this.select((state) => state.page);
  readonly actionToEdit$: Observable<ApiBuilderAction | undefined> = this.select((state) =>
    state.actions.find((action) => action.id === state.editingActionId),
  );
  readonly connectorName$: Observable<string> = this.select((state) => state.connectorName);
  readonly valid$: Observable<boolean> = this.select((state) => !!state.connectorName);
  readonly oAuthAuthorized$: Observable<boolean> = this.select(
    (state) => !!state.common.oAuth2Authorization?.isAuthorized,
  );
  readonly originalConnector$: Observable<ConnectorConfiguration | null> = this.select(
    (state) => state.originalConnector,
  );

  readonly coupledConnector$: Observable<ConnectorConfiguration | null> = this.select(
    (state) => state.coupledConnector,
  );
  readonly savingConnector$: Observable<boolean> = this.select((state) => state.savingConnector);

  // EFFECTS
  readonly testAction = this.effect(
    (payload$: Observable<{ accountId: string; action: ApiBuilderAction }>) => {
      return payload$.pipe(
        tap(({ action }) => this.setActionStatus(action, ApiActionStatus.TESTING)),
        withLatestFrom(this.common$, this.connectorId$),
        switchMap(([{ accountId, action }, common, connectorId]) => {
          const request: TestRestApiRequest = {
            accountId,
            actionName: action.data.name,
            apiDefinition: this.apiBuilderService.mapStoreToDefinition(common, [action]),
            connectorId,
            parameters: action.parameters,
          };

          /**
           * Clear result path before testing api.
           */
          request.apiDefinition.actions[0].resultPath = '';

          return this.apiBuilderApiService.postTestAction(request).pipe(
            tap(({ response, resultColumns, noContent }) => {
              this.setState((state) =>
                produce(state, (draft) => {
                  const index = getActionIndex(draft, action);
                  const draftAction = draft.actions[index];
                  draftAction.status = ApiActionStatus.STAGING;
                  draftAction.response = response;
                  draftAction.data.resultColumns = resultColumns;
                  draftAction.data.noContent = noContent;
                }),
              );
            }),
            catchError((error) => {
              console.error(error, action);
              this.notificationService.showError(error);
              this.setActionStatus(action, ApiActionStatus.ERROR);
              return EMPTY;
            }),
          );
        }),
        untilDestroyed(this),
      );
    },
  );

  readonly upsertConnector = this.effect(
    (payload$: Observable<{ accountId: string; onSuccess?: () => void }>) => {
      return payload$.pipe(
        tap(() => {
          this.setSavingConnector(true);
        }),
        withLatestFrom(
          this.connectorId$,
          this.connectorName$,
          this.common$,
          this.actions$,
          this.coupledConnector$,
        ),
        switchMap(
          ([{ accountId }, connectorId, connectorName, common, actions, coupledConnector]) => {
            const data = {
              accountId,
              connectorId,
              connectorName,
              common,
              actions,
              confirmed: true,
            };

            if (coupledConnector) {
              const { actions: coupledActions } = this.apiBuilderService.mapApiDefinitionToStore(
                coupledConnector?.customApiDefinition,
              );
              const actionsMismatch = !coupledActions.every((coupledAction) =>
                actions.find((action) => action.data.name === coupledAction.data.name),
              );

              if (actionsMismatch) {
                return this.dialogService
                  .openConfirmDialog({
                    title: 'Actions mismatch',
                    description:
                      'Action names are different between multiple environments.<br />Updating connector will change actions for every environment.<br />Do you want to proceed?',
                    cancelLabel: 'Cancel',
                    confirmLabel: 'Ok',
                  })
                  .afterClosed()
                  .pipe(
                    switchMap((confirmed) =>
                      of({
                        ...data,
                        confirmed: !!confirmed,
                      }),
                    ),
                  );
              }
            }

            return of(data);
          },
        ),
        tap(({ confirmed }) => {
          if (!confirmed) {
            this.setSavingConnector(false);
          }
        }),
        filter(({ confirmed }) => confirmed),
        switchMap(({ accountId, connectorId, connectorName, common, actions }) => {
          const request: CreateRestApiConnectorRequest = {
            name: connectorName,
            accountId,
            apiDefinition: this.apiBuilderService.mapStoreToDefinition(common, actions),
          };

          const upsert$ = connectorId
            ? this.apiBuilderApiService.putUpdateConnector(connectorId, request)
            : this.apiBuilderApiService.postCreateConnector(request);

          return upsert$.pipe(
            catchError((error) => {
              console.error(error);
              this.setSavingConnector(false);
              this.notificationService.showError(error);
              return EMPTY;
            }),
            tap((connector) => {
              this.setSavingConnector(false);
              this.apiBuilderService.notifyConnectorUpdated(connector);
            }),
          );
        }),
        untilDestroyed(this),
      );
    },
  );

  readonly confirmAction = this.effect((payload$: Observable<ApiBuilderAction>) => {
    return payload$.pipe(tap((action) => this.setActionStatus(action, ApiActionStatus.CONFIRMED)));
  });

  readonly setResultPath = this.effect(
    (
      payload$: Observable<{
        accountId: string;
        actionId: number;
        resultPath: string;
        successCallback: () => void;
      }>,
    ) => {
      return payload$.pipe(
        concatLatestFrom(() => this.actions$),
        map(([{ accountId, actionId, resultPath, successCallback }, actions]) => {
          const action = actions.find((a) => a.id === actionId);

          if (!action) {
            throw new Error('Action not found');
          }

          return {
            accountId,
            action,
            resultPath,
            successCallback,
          };
        }),
        tap(() => {
          this.setSavingConnector(true);
        }),
        switchMap(({ accountId, action, resultPath, successCallback }) =>
          this.apiBuilderApiService
            .postTestPath({ accountId, data: action.response, resultPath })
            .pipe(
              tap(({ resultColumns }) => {
                this.setActionResultPathAndColumns({
                  action,
                  columns: resultColumns,
                  path: resultPath,
                });
                successCallback();
                this.setSavingConnector(false);
              }),
              catchError((error) => {
                this.notificationService.showError(error);
                this.setSavingConnector(false);
                return EMPTY;
              }),
            ),
        ),
        untilDestroyed(this),
      );
    },
  );

  readonly openOAuthPopup = this.effect((payload$: Observable<void>) => {
    return payload$.pipe(
      switchMap(() =>
        this.common$.pipe(
          take(1),
          map((common) => {
            if (!common.oAuth2Authorization) {
              throw new Error('No oauth data found');
            }
            return common.oAuth2Authorization as OAuth2Authorization;
          }),
          switchMap((data) => {
            return this.apiBuilderApiService
              .getOAuthAuthorizeUrl({
                authorizeUrl: data.authorizeUrl,
                clientId: data.clientId,
                scope: data.scope || '',
              })
              .pipe(
                switchMap(({ url }) =>
                  this.oAuthService.openPopup(url, {
                    width: '700px',
                    height: '500px',
                  }),
                ),
                switchMap((ref) => this.oAuthService.getAuthorizationMessage(ref)),
                switchMap((message) =>
                  this.apiBuilderApiService.postInstallOAuth({
                    code: message.code,
                    state: message.state,
                    clientId: data.clientId,
                    clientSecret: data.clientSecret,
                    scope: data.scope,
                    tokenUrl: data.tokenUrl,
                  }),
                ),
                untilDestroyed(this),
              );
          }),
          tap((oAuthInfo) => {
            this.setOAuthInfo(oAuthInfo);
            this.setOAuthAuthorized(true);
          }),
          catchError((error) => {
            this.notificationService.showError(error);
            return EMPTY;
          }),
        ),
      ),
    );
  });

  // UPDATERS
  initialize = this.updater<{
    connectorId?: string;
    connectorName: string;
    common: ApiBuilderCommonConfig;
    actions: ApiBuilderAction[];
  }>((state, payload) =>
    produce(state, (draft) => {
      draft.connectorId = payload.connectorId;
      draft.connectorName = payload.connectorName;
      draft.common = payload.common;
      draft.actions = payload.actions.length
        ? payload.actions
        : [this.apiBuilderService.createNewAction()];
      draft.page = ApiBuilderDialogPage.Builder;
    }),
  );

  setConnectorId = this.updater<string | undefined>((state, connectorId) =>
    produce(state, (draft) => {
      draft.connectorId = connectorId;
    }),
  );

  setConnectorName = this.updater<string>((state, connectorName) =>
    produce(state, (draft) => {
      draft.connectorName = connectorName;
    }),
  );

  setCommonConfig = this.updater<ApiBuilderCommonConfig>((state, payload) =>
    produce(state, (draft) => {
      draft.common = payload;
    }),
  );

  setOAuthInfo = this.updater<OAuthInfo>((state, payload) =>
    produce(state, (draft) => {
      if (draft.common.oAuth2Authorization) {
        draft.common.oAuth2Authorization = { ...draft.common.oAuth2Authorization, ...payload };
      }
    }),
  );

  addAction = this.updater((state) =>
    produce(state, (draft) => {
      draft.actions.push(this.apiBuilderService.createNewAction());
    }),
  );

  updateAction = this.updater<ApiBuilderAction>((state, payload) =>
    produce(state, (draft) => {
      const index = getActionIndex(draft, payload);
      draft.actions[index] = payload;
    }),
  );

  setActionParametersTestValues = this.updater<{
    action: ApiBuilderAction;
    parameters?: ApiBuilderTestParameterDefinition[];
  }>((state, payload) =>
    produce(state, (draft) => {
      const index = getActionIndex(draft, payload.action);
      if (!payload.parameters) {
        draft.actions[index].parameters = null;
        return;
      }

      const mappedParameters: Record<string, any> = {};
      payload.parameters.forEach((param) => {
        mappedParameters[param.name] = param.value;
      });
      draft.actions[index].parameters = mappedParameters;
      draft.actions[index].data.isTested = true;
    }),
  );

  deleteAction = this.updater<ApiBuilderAction>((state, payload) =>
    produce(state, (draft) => {
      const index = getActionIndex(draft, payload);
      draft.actions.splice(index, 1);
    }),
  );

  setActionExpanded = this.updater<{ action: ApiBuilderAction; expanded: boolean }>(
    (state, payload) =>
      produce(state, (draft) => {
        const index = getActionIndex(draft, payload.action);
        draft.actions[index].expanded = payload.expanded;
      }),
  );

  setActionResultPathAndColumns = this.updater<{
    action: ApiBuilderAction;
    columns: ColumnInfo[];
    path: string;
  }>((state, payload) =>
    produce(state, (draft) => {
      const index = getActionIndex(draft, payload.action);
      const action = draft.actions[index];
      if (action) {
        action.resultPath = payload.path;
        action.data.resultColumns = payload.columns;
      }
    }),
  );

  openBuilderPage = this.updater((state) =>
    produce(state, (draft) => {
      draft.page = ApiBuilderDialogPage.Builder;
      draft.editingActionId = undefined;
    }),
  );

  openResultPathSelectPage = this.updater<ApiBuilderAction>((state, payload) =>
    produce(state, (draft) => {
      draft.page = ApiBuilderDialogPage.ResultPathSelect;
      draft.editingActionId = payload.id;
    }),
  );

  openBodyEditorPage = this.updater<ApiBuilderAction>((state, payload) =>
    produce(state, (draft) => {
      draft.page = ApiBuilderDialogPage.BodyEditor;
      draft.editingActionId = payload.id;
    }),
  );

  setOAuthAuthorized = this.updater<boolean>((state, authorized) =>
    produce(state, (draft) => {
      if (draft.common.oAuth2Authorization) {
        draft.common.oAuth2Authorization.isAuthorized = authorized;
      }
    }),
  );

  setOriginalConnector = this.updater<ConnectorConfiguration | null>((state, payload) =>
    produce(state, (draft) => {
      draft.originalConnector = payload;
    }),
  );

  setCoupledConnector = this.updater<ConnectorConfiguration | null>((state, payload) =>
    produce(state, (draft) => {
      draft.coupledConnector = payload;
    }),
  );

  setActionStatus = (pendingAction: ApiBuilderAction, status: ApiActionStatus): void =>
    this.patchState((state) => {
      return produce(state, (draft) => {
        const index = getActionIndex(draft, pendingAction);
        draft.actions[index].status = status;
      });
    });

  setSavingConnector = (value: boolean): void =>
    this.patchState((state) =>
      produce(state, (draft) => {
        draft.savingConnector = value;
      }),
    );

  constructor() {
    super(initialState);
  }
}
